import { Cartesian3, Color, Quaternion } from "cesium";
// ============================================
// Cesium → Plain conversion
// ============================================
const toPlainCartesian3 = (c) => ({
    x: c.x,
    y: c.y,
    z: c.z,
});
const toPlainQuaternion = (q) => ({
    x: q.x,
    y: q.y,
    z: q.z,
    w: q.w,
});
const toPlainColor = (c) => ({
    red: c.red,
    green: c.green,
    blue: c.blue,
    alpha: c.alpha,
});
// ShapeConfig conversion (shapeType included)
export const toPlainBoxShapeConfig = (box) => ({
    shapeType: "box",
    localCenter: toPlainCartesian3(box.localCenter),
    wgs84Center: toPlainCartesian3(box.wgs84Center),
    translation: toPlainCartesian3(box.translation),
    quaternion: toPlainQuaternion(box.quaternion),
    scale: toPlainCartesian3(box.scale),
    dimensions: toPlainCartesian3(box.dimensions),
    color: toPlainColor(box.color),
});
export const toPlainSphereShapeConfig = (sphere) => ({
    shapeType: "sphere",
    localCenter: toPlainCartesian3(sphere.localCenter),
    wgs84Center: toPlainCartesian3(sphere.wgs84Center),
    translation: toPlainCartesian3(sphere.translation),
    quaternion: toPlainQuaternion(sphere.quaternion),
    scale: toPlainCartesian3(sphere.scale),
    dimensions: toPlainCartesian3(sphere.dimensions),
    color: toPlainColor(sphere.color),
});
export const toPlainCylinderShapeConfig = (cylinder) => ({
    shapeType: "cylinder",
    localCenter: toPlainCartesian3(cylinder.localCenter),
    wgs84Center: toPlainCartesian3(cylinder.wgs84Center),
    translation: toPlainCartesian3(cylinder.translation),
    quaternion: toPlainQuaternion(cylinder.quaternion),
    scale: toPlainCartesian3(cylinder.scale),
    dimensions: toPlainCartesian3(cylinder.dimensions),
    color: toPlainColor(cylinder.color),
});
// Full SavedData conversion (shapeConfig + attributes)
// id is passed separately from TAttributes
// name is stored at top-level by Client, not in attributes
export const toBoxSavedData = (id, box, attributes, options) => ({
    id,
    shapeConfig: toPlainBoxShapeConfig(box),
    attributes: {
        anchorType: attributes.anchorType,
        ...(options?.baselineQuaternion
            ? { baselineQuaternion: options.baselineQuaternion }
            : {}),
        ...(attributes.bottomZ !== undefined
            ? { bottomZ: attributes.bottomZ }
            : {}),
    },
});
export const toSphereSavedData = (id, sphere, attributes, options) => ({
    id,
    shapeConfig: toPlainSphereShapeConfig(sphere),
    attributes: {
        anchorType: attributes.anchorType,
        ...(options?.baselineQuaternion
            ? { baselineQuaternion: options.baselineQuaternion }
            : {}),
        ...(attributes.bottomZ !== undefined
            ? { bottomZ: attributes.bottomZ }
            : {}),
    },
});
export const toCylinderSavedData = (id, cylinder, attributes, options) => ({
    id,
    shapeConfig: toPlainCylinderShapeConfig(cylinder),
    attributes: {
        anchorType: attributes.anchorType,
        ...(options?.baselineQuaternion
            ? { baselineQuaternion: options.baselineQuaternion }
            : {}),
        ...(attributes.bottomZ !== undefined
            ? { bottomZ: attributes.bottomZ }
            : {}),
    },
});
// ============================================
// Plain → Cesium conversion
// ============================================
const toCartesian3 = (p) => new Cartesian3(p.x, p.y, p.z);
const toQuaternion = (q) => new Quaternion(q.x, q.y, q.z, q.w);
const toColor = (c) => new Color(c.red, c.green, c.blue, c.alpha);
export const fromPlainBoxShapeConfig = (plain) => ({
    shapeType: "box",
    localCenter: toCartesian3(plain.localCenter),
    wgs84Center: toCartesian3(plain.wgs84Center),
    translation: toCartesian3(plain.translation),
    quaternion: toQuaternion(plain.quaternion),
    scale: toCartesian3(plain.scale),
    dimensions: toCartesian3(plain.dimensions),
    color: toColor(plain.color),
});
export const fromPlainSphereShapeConfig = (plain) => ({
    shapeType: "sphere",
    localCenter: toCartesian3(plain.localCenter),
    wgs84Center: toCartesian3(plain.wgs84Center),
    translation: toCartesian3(plain.translation),
    quaternion: toQuaternion(plain.quaternion),
    scale: toCartesian3(plain.scale),
    dimensions: toCartesian3(plain.dimensions),
    color: toColor(plain.color),
});
export const fromPlainCylinderShapeConfig = (plain) => ({
    shapeType: "cylinder",
    localCenter: toCartesian3(plain.localCenter),
    wgs84Center: toCartesian3(plain.wgs84Center),
    translation: toCartesian3(plain.translation),
    quaternion: toQuaternion(plain.quaternion),
    scale: toCartesian3(plain.scale),
    dimensions: toCartesian3(plain.dimensions),
    color: toColor(plain.color),
});
// From SavedData
export const fromBoxSavedData = (saved) => fromPlainBoxShapeConfig(saved.shapeConfig);
export const fromSphereSavedData = (saved) => fromPlainSphereShapeConfig(saved.shapeConfig);
export const fromCylinderSavedData = (saved) => fromPlainCylinderShapeConfig(saved.shapeConfig);
export const fromSavedData = (saved) => {
    switch (saved.shapeConfig.shapeType) {
        case "box":
            return fromBoxSavedData(saved);
        case "sphere":
            return fromSphereSavedData(saved);
        case "cylinder":
            return fromCylinderSavedData(saved);
        default:
            throw new Error(saved.shapeConfig);
    }
};
//# sourceMappingURL=solid-mesh-data.js.map